import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Add from "@mui/icons-material/Add";
import Autorenew from "@mui/icons-material/Autorenew";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import PauseCircleOutline from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutline from "@mui/icons-material/PlayCircleOutline";
import ReceiptLong from "@mui/icons-material/ReceiptLong";
import Send from "@mui/icons-material/Send";
import Visibility from "@mui/icons-material/Visibility";
import ListItemIcon from "@mui/material/ListItemIcon";
import Tooltip from "@mui/material/Tooltip";
import { willNeverHappen } from "healthex-common/utils";
import { WebhookConfigurationStatus } from "healthex-common/webhook.dtos";
import { observer } from "mobx-react";
import * as React from "react";
import { addDisplayName } from "../Infra/display-name";
import { NOT_LOADED } from "../Infra/RemoteData";
import { AddOrEditWebhookForm } from "./AddOrEditWebhookForm";
import { AdminWebhookListViewModel } from "./AdminWebhookListViewModel";
import { ActionButton, AddWebhookDialogHeader, AdminWebhooksViewParent, ChipActive, ChipAutoPaused, ChipMore, ChipNeutral, ChipOrgWide, DestructiveText, EmptyActions, EmptyEventCode, EmptyMark, EmptyText, EmptyTitle, HealthBar, HealthTick, IntroAndActionsRow, IntroText, MonoText, ProjectScopeCell, WebhookEmptyState, } from "./AdminWebhooksListView.styles";
import { BaseLink } from "./base.styles";
import { LoadingSpinner } from "./DocumentRetrievalReviewAndShare.styles";
import { HXButton } from "./HXButton";
import { HXDataGrid } from "./HXDataGrid";
import { LightSmallText } from "./HXDesktopPage.styles";
import { HXDialog, HXDialogCloseButton, HXDialogContent, HXDialogHeader, HXDialogTitle, HXDialogTitleAndSubtitleContainer, } from "./HXDialog";
import HXMenuItem from "./HXMenuItem";
import { HXSimpleDialog } from "./HXSimpleDialog";
import { useViewModel } from "./useViewModel";
import { WebhookSecretRevealView } from "./WebhookSecretReveal";
export const AdminWebhooksListView = observer((props) => {
    const model = useViewModel(AdminWebhookListViewModel, props);
    if (model.webhooks === NOT_LOADED)
        return _jsx(LoadingSpinner, {});
    const docsUrl = `${props.container.environment.DOCS_BASE_URL}/api-documentation/webhooks`;
    return (_jsxs(AdminWebhooksViewParent, { children: [model.total === 0 ? (_jsxs(WebhookEmptyState, { children: [_jsx(EmptyMark, {}), _jsx(EmptyTitle, { children: "No webhooks yet" }), _jsxs(EmptyText, { children: ["Webhooks send event notifications from HealthEx to your applications in real time. Start with", " ", _jsx(EmptyEventCode, { children: "consent.withdrawn" }), " to receive an event whenever a patient revokes access to their data for one of your projects."] }), _jsxs(EmptyActions, { children: [_jsx(HXButton, { onClick: () => model.addWebhook(), children: "Add webhook" }), _jsx(BaseLink, { href: docsUrl, children: "Read the docs \u2192" })] })] })) : (_jsxs(_Fragment, { children: [_jsxs(IntroAndActionsRow, { children: [_jsxs(IntroText, { children: ["Register HTTPS endpoints to receive real-time event notifications for your projects.", " ", _jsx(BaseLink, { href: docsUrl, children: "View documentation" }), "."] }), _jsx(HXButton, { secondary: true, startIcon: _jsx(Add, {}), onClick: () => model.addWebhook(), children: "Add webhook" })] }), _jsx(HXDataGrid, { rows: model.webhooks, columns: columns(), getRowId: (webhook) => webhook.id, rowCount: model.total, paginationModel: model.paginationInput, onPaginationModelChange: (paginationModel) => model.updatePagination(paginationModel), paginationSelectedLabel: "webhook" })] })), _jsxs(HXDialog, { open: model.addWebhookDialogOpen, onClose: () => model.closeAddWebhook(false), width: 560, "aria-labelledby": "add-webhook-title", children: [_jsxs(AddWebhookDialogHeader, { children: [_jsxs(HXDialogTitleAndSubtitleContainer, { children: [_jsx(HXDialogTitle, { id: "add-webhook-title", children: "Add webhook" }), _jsx(LightSmallText, { children: "Register an HTTPS endpoint to receive event notifications. You can edit any of these settings later." })] }), _jsx(HXDialogCloseButton, { onClick: () => model.closeAddWebhook(false) })] }), _jsx(HXDialogContent, { children: _jsx(AddOrEditWebhookForm, { container: props.container, onCreated: (result, sendTestEvent) => model.onWebhookCreated(result, sendTestEvent), onCancel: () => model.closeAddWebhook(false) }) })] }), model.secretRevealState && (_jsxs(HXDialog, { open: true, onClose: () => model.closeSecretReveal(), width: 560, "aria-labelledby": "webhook-secret-title", children: [_jsxs(HXDialogHeader, { children: [_jsx(HXDialogTitleAndSubtitleContainer, { children: _jsx(HXDialogTitle, { id: "webhook-secret-title", children: model.secretRevealState.kind === "created"
                                        ? "Webhook created — save your signing secret"
                                        : "Signing secret rotated — save the new secret" }) }), _jsx(HXDialogCloseButton, { onClick: () => model.closeSecretReveal() })] }), _jsx(HXDialogContent, { children: _jsx(WebhookSecretRevealView, { container: props.container, kind: model.secretRevealState.kind, webhookName: model.secretRevealState.webhookName, signingSecret: model.secretRevealState.signingSecret, testEventSent: model.secretRevealState.testEventSent, onDone: () => model.closeSecretReveal() }) })] })), _jsx(HXSimpleDialog, { open: model.deleteConfirmationDialogModel.open, onClose: (choice) => model.deleteConfirmationDialogModel.onChoice(choice), closeChoice: false, title: "Delete webhook", buttons: [
                    [true, "Yes, delete", { color: "error" }],
                    [false, "Not now"],
                ], children: "Are you sure you want to delete this webhook? All webhook deliveries will stop immediately and webhook history will be lost." }), _jsxs(HXSimpleDialog, { open: model.rotateConfirmationDialogModel.open, onClose: (choice) => model.rotateConfirmationDialogModel.onChoice(choice), closeChoice: false, title: "Rotate secret", buttons: [
                    [true, "Yes, rotate", { color: "error" }],
                    [false, "Not now"],
                ], children: ["Are you sure you want to rotate the secret for this webhook? This action cannot be undone.", _jsx("br", {}), _jsx("br", {}), "The existing secret will continue to work for a grace period of 24 hours."] })] }));
    function columns() {
        return [
            { field: "name", headerName: "Name", flex: 1, sortable: false },
            {
                field: "endpointUrl",
                headerName: "Endpoint",
                flex: 1.5,
                sortable: false,
                cellClassName: "singleLineEllipis",
                renderCell: (params) => {
                    if (params.value === undefined)
                        return null;
                    return _jsx(MonoText, { children: params.value });
                },
            },
            {
                field: "projectScope",
                headerName: "Project scope",
                flex: 1,
                sortable: false,
                renderCell: (params) => {
                    if (params.value === undefined)
                        return null;
                    return projectScopeChip(params.value);
                },
            },
            {
                field: "events",
                headerName: "Events",
                flex: 1,
                sortable: false,
                renderCell: (params) => {
                    if (params.value === undefined)
                        return null;
                    return _jsx(MonoText, { children: params.value.join(", ") });
                },
            },
            {
                field: "status",
                headerName: "Status",
                flex: 0.8,
                sortable: false,
                renderCell: (params) => {
                    if (params.value === undefined)
                        return null;
                    return statusChip(params.value);
                },
            },
            { field: "lastDeliveryLabel", headerName: "Last delivery", flex: 0.7, sortable: false },
            {
                field: "recentHealth",
                headerName: "Health",
                flex: 0.6,
                sortable: false,
                renderCell: (params) => {
                    if (params.value === undefined)
                        return null;
                    return (_jsx(HealthBar, { children: params.value.map((outcome, index) => (_jsx(HealthTick, { outcome: outcome }, index))) }));
                },
            },
            {
                field: "actions",
                headerName: "Actions",
                flex: 0.4,
                sortable: false,
                renderCell: (params) => {
                    const webhook = params.row;
                    const pauseResume = pauseResumeAction(webhook.status);
                    return (_jsxs(ActionButton, { children: [_jsxs(HXMenuItem, { onClick: () => model.viewDetails(webhook), children: [_jsx(ListItemIcon, { children: _jsx(Visibility, { fontSize: "small" }) }), "View details"] }), _jsxs(HXMenuItem, { onClick: () => model.viewDeliveryLog(webhook), children: [_jsx(ListItemIcon, { children: _jsx(ReceiptLong, { fontSize: "small" }) }), "View delivery log"] }), _jsxs(HXMenuItem, { onClick: () => model.rotateSecret(webhook), children: [_jsx(ListItemIcon, { children: _jsx(Autorenew, { fontSize: "small" }) }), "Rotate secret"] }), _jsxs(HXMenuItem, { onClick: () => model.sendTestEvent(webhook), children: [_jsx(ListItemIcon, { children: _jsx(Send, { fontSize: "small" }) }), "Send test event"] }), _jsxs(HXMenuItem, { onClick: () => model.pauseOrResume(webhook), children: [_jsx(ListItemIcon, { children: pauseResume.icon }), pauseResume.label] }), _jsxs(HXMenuItem, { onClick: () => model.deleteWebhook(webhook), children: [_jsx(ListItemIcon, { children: _jsx(DeleteOutline, { fontSize: "small" }) }), _jsx(DestructiveText, { children: "Delete webhook" })] })] }));
                },
            },
        ];
    }
});
function statusChip(status) {
    switch (status) {
        case WebhookConfigurationStatus.ACTIVE:
            return _jsx(ChipActive, { label: "Active" });
        case WebhookConfigurationStatus.PAUSED:
            return _jsx(ChipNeutral, { label: "Paused (manual)" });
        case WebhookConfigurationStatus.PAUSED_AUTOMATICALLY:
            return _jsx(ChipAutoPaused, { label: "Paused (auto)" });
    }
    return willNeverHappen(status);
}
function projectScopeChip(scope) {
    if (scope.orgWide) {
        return _jsx(ChipOrgWide, { label: "All projects" });
    }
    const [firstProject, ...remainingProjects] = scope.projects;
    if (firstProject === undefined)
        return null;
    return (_jsxs(ProjectScopeCell, { children: [_jsx(Tooltip, { title: firstProject, children: _jsx(ChipNeutral, { label: firstProject }) }), remainingProjects.length > 0 && (_jsx(Tooltip, { title: remainingProjects.join(", "), children: _jsx(ChipMore, { label: `+${remainingProjects.length} more` }) }))] }));
}
function pauseResumeAction(status) {
    if (status === WebhookConfigurationStatus.ACTIVE) {
        return { label: "Pause", icon: _jsx(PauseCircleOutline, { fontSize: "small" }) };
    }
    if (status === WebhookConfigurationStatus.PAUSED_AUTOMATICALLY) {
        return { label: "Re-enable", icon: _jsx(PlayCircleOutline, { fontSize: "small" }) };
    }
    return { label: "Resume", icon: _jsx(PlayCircleOutline, { fontSize: "small" }) };
}
addDisplayName(AdminWebhooksListView, "AdminWebhooksListView");
